/**
 * Cliente HTTP del andamiaje.
 *
 * Las rutas son relativas a propósito: en desarrollo el proxy de Vite manda
 * `/api` y `/salud` al backend (ver `vite.config.ts`), y en producción ambos
 * cuelgan del mismo origen detrás de Traefik.
 *
 * NOTA DE CONTRATO: el backend se está escribiendo en paralelo y el formato
 * exacto de sus respuestas todavía no está fijado en ningún documento. Por eso
 * la lectura de aquí es tolerante: acepta `snake_case` y `camelCase`, y acepta
 * tanto un arreglo suelto como un objeto envolvente. Cuando el contrato quede
 * escrito, esto se aprieta.
 */

const TIEMPO_LIMITE_MS = 5000

/* --- Salud ---------------------------------------------------------------- */

export type ClaseSalud = 'listo' | 'sin-base' | 'sin-respuesta'

export interface EstadoBackend {
  clase: ClaseSalud
  detalle: string
}

/**
 * Consulta `GET /salud/listo`.
 *
 * No lanza: los tres desenlaces son información útil para la pantalla, no
 * fallas del cliente.
 */
export async function consultarSalud(): Promise<EstadoBackend> {
  let respuesta: Response
  try {
    respuesta = await fetch('/salud/listo', {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    return {
      clase: 'sin-respuesta',
      detalle: `No se pudo contactar al backend (${describirError(error)}).`,
    }
  }

  const cuerpo = await leerJsonTolerante(respuesta)

  if (!respuesta.ok) {
    return {
      clase: 'sin-base',
      detalle:
        mensajeDelCuerpo(cuerpo) ??
        `El backend respondió ${respuesta.status} en /salud/listo.`,
    }
  }

  if (baseCaidaSegunCuerpo(cuerpo)) {
    return {
      clase: 'sin-base',
      detalle:
        mensajeDelCuerpo(cuerpo) ??
        'El backend responde, pero reporta que la base de datos no está disponible.',
    }
  }

  return {
    clase: 'listo',
    detalle: 'El backend responde y reporta la base de datos disponible.',
  }
}

/* --- Espacios ------------------------------------------------------------- */

/**
 * Clave de TanStack Query del catálogo.
 *
 * Se exporta porque ya son dos las pantallas que lo leen —el catálogo público y
 * el panel de administración— y una de ellas lo modifica. Con la clave repetida
 * a mano en cada sitio, una edición aceptada dejaría la otra pantalla mostrando
 * el catálogo anterior.
 */
export const CLAVE_ESPACIOS = ['espacios'] as const

export interface Espacio {
  id: string
  nombre: string | null
  tipo: string | null
  modo: string | null
  ubicacion: string | null
  /** NULL en el origen = la capacidad no está declarada. Nunca es 0. */
  capacidad: number | null
  /** NULL en el origen = sin límite declarado. */
  duracionMax: string | null
  /** NULL en el origen = dato pendiente de confirmar con la escuela. */
  requiereLlave: boolean | null
  activo: boolean | null
  /**
   * `estado` del ENUM `estado_espacio` (migración 014): `disponible`,
   * `mantenimiento` o `baja`.
   *
   * `null` significa que ESTE SERVIDOR NO LO REPORTA todavía, no que el espacio
   * no tenga estado: la columna es NOT NULL en la base. `activo` es una columna
   * generada a partir de `estado`, así que sirve de respaldo para distinguir
   * «se puede reservar» de «no», pero NO alcanza para separar un mantenimiento
   * temporal de una baja definitiva. Quien lo muestre tiene que decirlo en vez
   * de suponer uno de los dos.
   */
  estado: string | null
  /** `nota_estado`: por qué está fuera de servicio. Se le muestra al alumno. */
  notaEstado: string | null
}

/** Consulta `GET /api/espacios`. Lanza si la petición no llega o no es 2xx. */
export async function consultarEspacios(): Promise<Espacio[]> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/espacios', {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo contactar al backend (${describirError(error)}).`,
    )
  }

  const cuerpo = await leerJsonTolerante(respuesta)

  if (!respuesta.ok) {
    throw new Error(
      mensajeDelCuerpo(cuerpo) ??
        `El backend respondió ${respuesta.status} en /api/espacios.`,
    )
  }

  const filas = extraerLista(cuerpo)
  if (filas === null) {
    throw new Error(
      'El backend respondió algo que no es una lista de espacios.',
    )
  }

  return filas.map(normalizarEspacio)
}

function normalizarEspacio(fila: unknown): Espacio {
  const registro = esRegistro(fila) ? fila : {}
  return {
    id: texto(registro, 'id') ?? '',
    nombre: texto(registro, 'nombre'),
    tipo: texto(registro, 'tipo'),
    modo: texto(registro, 'modo'),
    ubicacion: texto(registro, 'ubicacion'),
    capacidad: numero(registro, 'capacidad'),
    duracionMax: texto(registro, 'duracion_max', 'duracionMax'),
    requiereLlave: booleano(registro, 'requiere_llave', 'requiereLlave'),
    activo: booleano(registro, 'activo'),
    estado: texto(registro, 'estado'),
    notaEstado: texto(registro, 'nota_estado', 'notaEstado'),
  }
}

/* --- Lectura tolerante ---------------------------------------------------- */

function esRegistro(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
}

function primerValor(
  registro: Record<string, unknown>,
  claves: string[],
): unknown {
  for (const clave of claves) {
    const valor = registro[clave]
    if (valor !== undefined) return valor
  }
  return undefined
}

/** Devuelve null cuando el dato falta o es NULL: es un hueco, no un vacío. */
function texto(
  registro: Record<string, unknown>,
  ...claves: string[]
): string | null {
  const valor = primerValor(registro, claves)
  if (typeof valor === 'string' && valor.trim() !== '') return valor
  if (typeof valor === 'number') return String(valor)
  return null
}

function numero(
  registro: Record<string, unknown>,
  ...claves: string[]
): number | null {
  const valor = primerValor(registro, claves)
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor
  if (typeof valor === 'string' && valor.trim() !== '') {
    const convertido = Number(valor)
    if (Number.isFinite(convertido)) return convertido
  }
  return null
}

function booleano(
  registro: Record<string, unknown>,
  ...claves: string[]
): boolean | null {
  const valor = primerValor(registro, claves)
  if (typeof valor === 'boolean') return valor
  if (valor === 'true') return true
  if (valor === 'false') return false
  return null
}

/** Acepta `[...]`, `{ datos: [...] }`, `{ espacios: [...] }`, `{ items: [...] }`. */
function extraerLista(cuerpo: unknown): unknown[] | null {
  if (Array.isArray(cuerpo)) return cuerpo
  if (esRegistro(cuerpo)) {
    for (const clave of ['datos', 'espacios', 'items', 'resultados', 'data']) {
      const valor = cuerpo[clave]
      if (Array.isArray(valor)) return valor
    }
  }
  return null
}

async function leerJsonTolerante(respuesta: Response): Promise<unknown> {
  try {
    return await respuesta.json()
  } catch {
    return null
  }
}

function mensajeDelCuerpo(cuerpo: unknown): string | null {
  if (!esRegistro(cuerpo)) return null
  const mensaje = primerValor(cuerpo, ['mensaje', 'message', 'error', 'detalle'])
  return typeof mensaje === 'string' && mensaje.trim() !== '' ? mensaje : null
}

/**
 * Heurística: el backend responde 200 pero declara la base caída. Se revisan
 * los nombres plausibles hasta que el contrato quede escrito.
 */
function baseCaidaSegunCuerpo(cuerpo: unknown): boolean {
  if (!esRegistro(cuerpo)) return false

  const indicadorBase = primerValor(cuerpo, [
    'baseDeDatos',
    'base_de_datos',
    'base',
    'bd',
    'db',
    'postgres',
  ])
  if (indicadorBase === false) return true
  if (typeof indicadorBase === 'string') {
    return ['abajo', 'caida', 'caída', 'down', 'error', 'fallida'].includes(
      indicadorBase.toLowerCase(),
    )
  }
  if (esRegistro(indicadorBase) && indicadorBase['ok'] === false) return true

  const estado = primerValor(cuerpo, ['estado', 'status'])
  if (typeof estado === 'string') {
    return ['degradado', 'degraded', 'sin_base', 'sin-base', 'error'].includes(
      estado.toLowerCase(),
    )
  }

  return false
}

function describirError(error: unknown): string {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return 'sin respuesta en 5 segundos'
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return 'error de red'
}
